import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/contexts/CookieContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute } from '@/lib/routes';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';

/**
 * Cookie Preferences Modal
 * 
 * Allows users to manage their cookie preferences with granular control.
 * Strictly necessary cookies cannot be disabled (always enabled).
 * Changes are not saved until user clicks "Save Preferences" or "Accept All"/"Reject All".
 */
export const CookiePreferences: React.FC = () => {
  const { language, t } = useLanguage();
  const {
    consent,
    isPreferencesOpen,
    closePreferences,
    updatePreferences,
    acceptAll,
    rejectAll,
    clearConsent,
  } = useCookieConsent();

  // Local state for temporary preferences (not saved until user clicks save)
  const [tempPreferences, setTempPreferences] = useState<{
    strictlyNecessary: boolean;
    analytics: boolean;
  }>({
    strictlyNecessary: true,
    analytics: true, // Default to true so checkbox is checked by default
  });

  // Update temp preferences when modal opens or consent changes
  useEffect(() => {
    if (isPreferencesOpen) {
      // When modal opens, initialize temp preferences from consent or defaults
      setTempPreferences(
        consent?.preferences || {
          strictlyNecessary: true,
          analytics: true, // Default to true so checkbox is checked by default
        }
      );
    }
  }, [isPreferencesOpen, consent]);

  const handleSave = () => {
    // Save the temporary preferences
    updatePreferences({
      analytics: tempPreferences.analytics,
    });
    // Close the modal after saving
    closePreferences();
  };

  const handleAnalyticsChange = (checked: boolean) => {
    // Only update local state, don't save yet
    setTempPreferences((prev) => ({
      ...prev,
      analytics: checked,
    }));
  };

  const handleAcceptAll = () => {
    // Set temp preferences to accept all, then save
    setTempPreferences({
      strictlyNecessary: true,
      analytics: true,
    });
    // Accept all will save and close the modal
    acceptAll();
  };

  const handleRejectAll = () => {
    // Set temp preferences to reject all, then save
    setTempPreferences({
      strictlyNecessary: true,
      analytics: false,
    });
    // Reject all will save and close the modal
    rejectAll();
  };

  // Handle dialog close - only close if user explicitly wants to (not on outside click)
  const handleDialogChange = (open: boolean) => {
    if (!open) {
      // If closing, reset temp preferences to current consent to discard unsaved changes
      setTempPreferences(
        consent?.preferences || {
          strictlyNecessary: true,
          analytics: true,
        }
      );
      closePreferences();
    }
  };

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('cookies.preferences.title')}</DialogTitle>
          <DialogDescription>
            {t('cookies.preferences.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Strictly Necessary Cookies */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="strictly-necessary" className="text-base font-semibold">
                  {t('cookies.preferences.strictlyNecessary.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.preferences.strictlyNecessary.description')}
                </p>
              </div>
              <Switch
                id="strictly-necessary"
                checked={true}
                disabled
                aria-label="Strictly necessary cookies (always enabled)"
                className="ml-4"
              />
            </div>
            <div className="text-xs text-muted-foreground pl-1">
              <p className="font-medium mb-1">{t('cookies.preferences.strictlyNecessary.examplesTitle')}</p>
              <ul className="list-disc list-inside space-y-1">
                {(t('cookies.preferences.strictlyNecessary.examples') as unknown as string[]).map((example: string, i: number) => (
                  <li key={i}>{example}</li>
                ))}
              </ul>
            </div>
          </div>

          <Separator />

          {/* Analytics Cookies */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  {t('cookies.preferences.analytics.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookies.preferences.analytics.description')}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={tempPreferences.analytics}
                onCheckedChange={handleAnalyticsChange}
                aria-label="Analytics cookies"
                className="ml-4"
              />
            </div>
            <div className="text-xs text-muted-foreground pl-1">
              <p className="font-medium mb-1">{t('cookies.preferences.analytics.examplesTitle')}</p>
              <ul className="list-disc list-inside space-y-1">
                {(t('cookies.preferences.analytics.examples') as unknown as string[]).map((example: string, i: number) => (
                  <li key={i}>{example}</li>
                ))}
              </ul>
              {!tempPreferences.analytics && (
                <p className="mt-2 italic">
                  {t('cookies.preferences.analytics.disabledNote')}
                </p>
              )}
              {tempPreferences.analytics && (
                <details className="mt-3">
                  <summary className="cursor-pointer font-medium hover:text-foreground">
                    {t('cookies.preferences.analytics.viewDetails')}
                  </summary>
                  <div className="mt-2 pl-2 space-y-2 border-l-2 border-muted">
                    <p className="text-xs">
                      {t('cookies.preferences.analytics.googleCookiesInfo')}
                    </p>
                    <ul className="text-xs space-y-1 list-disc list-inside">
                      {(t('cookies.preferences.analytics.googleCookies') as unknown as string[]).map((cookie: string, i: number) => {
                        // "name - description"; the Russian list uses an em dash.
                        const [name, ...descParts] = cookie.split(/\s[-–—]\s/);
                        const description = descParts.join(' – ');
                        return (
                          <li key={i}>
                            <code className="bg-muted px-1 rounded">{name}</code>
                            {description && ` - ${description}`}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-xs italic mt-2">
                      {t('cookies.preferences.analytics.googleCookiesNote')}
                    </p>
                  </div>
                </details>
              )}
            </div>
          </div>

          <Separator />

          {/* Additional Information */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="text-sm font-semibold">{t('cookies.preferences.privacySection.title')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('cookies.preferences.privacySection.description')}{' '}
              <a
                href={getLocalizedRoute('/politica-de-confidentialitate', language)}
                className="text-primary underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('cookies.preferences.privacySection.cookiePolicy')}
              </a>
              {' '}{t('cookies.preferences.privacySection.and')}{' '}
              <a
                href={getLocalizedRoute('/politica-de-confidentialitate', language)}
                className="text-primary underline hover:text-primary/80 focus:outline-none focus:ring-2 focus:ring-ring rounded"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('cookies.preferences.privacySection.privacyPolicy')}
              </a>
              .
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => {
              clearConsent();
              closePreferences();
            }}
            className="w-full sm:w-auto text-muted-foreground hover:text-destructive"
            aria-label="Clear all cookie preferences"
          >
            {t('cookies.preferences.buttons.clearAll')}
          </Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={handleRejectAll}
              className="flex-1 sm:flex-none"
              aria-label="Reject all non-essential cookies"
            >
              {t('cookies.preferences.buttons.rejectAll')}
            </Button>
            <Button
              variant="default"
              onClick={handleAcceptAll}
              className="flex-1 sm:flex-none"
              aria-label="Accept all cookies"
            >
              {t('cookies.preferences.buttons.acceptAll')}
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 sm:flex-none"
              aria-label="Save preferences"
            >
              {t('cookies.preferences.buttons.savePreferences')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

